import { useCallback, useEffect, useState } from 'react';

import { UserContainer } from '../providers';
import { useFormatNumber } from './use-format-number';

export interface ICurrency {
  code: string;
  decimalDigits: number;
  decimalSeparator: string;
  spaceBetweenAmountAndSymbol: boolean;
  symbol: string;
  symbolOnLeft: boolean;
  thousandsSeparator: string;
}

const useCurrency = () => {
  const { state: userState, setUser } = UserContainer.useContainer();
  const [currency, setCurrency] = useState<ICurrency>(
    userState?.user?.currency ?? {
      code: '',
      decimalDigits: 0,
      decimalSeparator: '',
      spaceBetweenAmountAndSymbol: false,
      symbol: '',
      symbolOnLeft: false,
      thousandsSeparator: '',
    }
  );

  const { numberDecimal } = useFormatNumber();

  useEffect(() => {
    if (userState?.user?.currency) {
      setCurrency(userState.user.currency);
    }
  }, [userState?.user?.currency]);

  const updateUserCurrency = useCallback(
    (newCurrency: ICurrency) => {
      setCurrency(newCurrency);
      const newUserCurrency = {
        ...userState.user,
        currency: newCurrency,
      };
      setUser(newUserCurrency);
    },
    [setUser, userState.user]
  );

  const getFormattedCurrency = useCallback(
    (amount: number) => {
      if (!amount && amount !== 0) {
        return '-';
      }
      const value = numberDecimal(amount);

      const { symbol, symbolOnLeft, spaceBetweenAmountAndSymbol } = currency;

      // determine padding string
      const padding = spaceBetweenAmountAndSymbol ? ' ' : '';

      // arrange formatted currency string and return
      return symbolOnLeft
        ? symbol + padding + value
        : value + padding + (symbol ?? '');
    },
    [currency, numberDecimal]
  );

  return {
    currency,
    updateUserCurrency,
    getFormattedCurrency,
  };
};

export default useCurrency;
